import React, { useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    flexRender,
    createColumnHelper,
} from '@tanstack/react-table';
import { Car } from '../entities/car.entities';
import Pagination from './Pagination';

interface CarListProps {
    cars: Car[];
    isLoading: boolean;
    isError: boolean;
    page: number;
    setPage: (page: number) => void;
    totalPages: number;
    isMobile: boolean;
}

const columnHelper = createColumnHelper<Car>();

const isValid = (value: any) => value !== null && value !== undefined && value !== 'NULL';

const formatValue = (value: any) => isValid(value) ? value : '-';

// Helper for conditional formatting
const getValueColor = (value: number) => {
    if (value < 80) return 'val-green';
    if (value >= 80 && value <= 100) return 'val-yellow';
    return 'val-red';
};

const columns = [
    columnHelper.accessor('Make', {
        header: 'Make',
        cell: info => formatValue(info.getValue()),
        size: 150,
    }),
    columnHelper.accessor('Model', {
        header: 'Model',
        cell: info => formatValue(info.getValue()),
        size: 150,
    }),
    columnHelper.accessor('Year', {
        header: 'Year',
        cell: info => formatValue(info.getValue()),
        size: 80,
    }),
    columnHelper.accessor('Body', {
        header: 'Body',
        cell: info => formatValue(info.getValue()),
        size: 100,
    }),
    columnHelper.accessor('Drivetrain', {
        header: 'Drivetrain',
        cell: info => formatValue(info.getValue()),
        size: 120,
    }),
    columnHelper.accessor('Average', {
        header: 'Average',
        cell: info => {
            const val = info.getValue();
            return isValid(val) ? <span className={getValueColor(val)}>{val}</span> : '-';
        },
        size: 100,
    }),
    columnHelper.accessor('Collision', {
        header: 'Collision',
        cell: info => {
            const val = info.getValue();
            return isValid(val) ? <span className={getValueColor(val)}>{val}</span> : '-';
        },
        size: 100,
    }),
    columnHelper.accessor('Comp', {
        header: 'Comp',
        cell: info => {
            const val = info.getValue();
            return isValid(val) ? <span className={getValueColor(val)}>{val}</span> : '-';
        },
        size: 80,
    }),
    columnHelper.accessor('DCPD', {
        header: 'DCPD',
        cell: info => {
            const val = info.getValue();
            return isValid(val) ? <span className={getValueColor(val)}>{val}</span> : '-';
        },
        size: 80,
    }),
    columnHelper.accessor('AB', {
        header: 'AB',
        cell: info => {
            const val = info.getValue();
            return isValid(val) ? <span className={getValueColor(val)}>{val}</span> : '-';
        },
        size: 80,
    }),
];

const CarList: React.FC<CarListProps> = ({ cars, isLoading, isError, page, setPage, totalPages, isMobile }) => {
    const [sorting, setSorting] = useState<SortingState>([]);

    const table = useReactTable({
        data: cars,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const { rows } = table.getRowModel();

    return (
        <section className="table-section">
            {isLoading ? (
                <div className="loading">Loading cars...</div>
            ) : isError ? (
                <div className="error">Error loading cars. Please try again.</div>
            ) : (
                <>
                    <div className="table-container">
                        {isMobile ? (
                            // Mobile Card View
                            rows.map((row) => {
                                const car = row.original;
                                return (
                                    <div
                                        key={row.id}
                                        className="car-card"
                                    >
                                        <div className="card-header">
                                            <h3>{formatValue(car.Make)} {formatValue(car.Model)}</h3>
                                            <span className="year-badge">{formatValue(car.Year)}</span>
                                        </div>
                                        <div className="card-body">
                                            <div className="card-row">
                                                <span>Body:</span> <span>{formatValue(car.Body)}</span>
                                            </div>
                                            <div className="card-row">
                                                <span>Drivetrain:</span> <span>{formatValue(car.Drivetrain)}</span>
                                            </div>
                                            <div className="card-metrics">
                                                <div className="metric">
                                                    <span className="label">Avg</span>
                                                    <span className="value">
                                                        {isValid(car.Average) ? <span className={getValueColor(car.Average)}>{car.Average}</span> : '-'}
                                                    </span>
                                                </div>
                                                <div className="metric">
                                                    <span className="label">Coll</span>
                                                    <span className="value">
                                                        {isValid(car.Collision) ? <span className={getValueColor(car.Collision)}>{car.Collision}</span> : '-'}
                                                    </span>
                                                </div>
                                                <div className="metric">
                                                    <span className="label">Comp</span>
                                                    <span className="value">
                                                        {isValid(car.Comp) ? <span className={getValueColor(car.Comp)}>{car.Comp}</span> : '-'}
                                                    </span>
                                                </div>
                                                <div className="metric">
                                                    <span className="label">DCPD</span>
                                                    <span className="value">
                                                        {isValid(car.DCPD) ? <span className={getValueColor(car.DCPD)}>{car.DCPD}</span> : '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            // Desktop Table View
                            <table>
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map(header => (
                                                <th key={header.id} onClick={header.column.getToggleSortingHandler()} style={{ width: header.getSize() }}>
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {{
                                                        asc: ' 🔼',
                                                        desc: ' 🔽',
                                                    }[header.column.getIsSorted() as string] ?? null}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.id}>
                                            {row.getVisibleCells().map(cell => (
                                                <td key={cell.id} style={{ width: cell.column.getSize() }}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                    />
                </>
            )}
        </section>
    );
};

export default CarList;
